import cn from 'classnames';
import { FC, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button, Icon, Spinner, Textarea } from '@/shared/ui';

import { type ComposerProps, patientAgentMessages } from '../config';
import { isEntitySubject } from '../lib';

import styles from './AgentChat.module.scss';
import { ComposerScrollbar } from './ComposerScrollbar';

export const Composer: FC<ComposerProps> = ({
  floating,
  isShell,
  documentMode,
  inputValue,
  onInputChange,
  onKeyDown,
  isRunning,
  isResolving,
  pendingAttachments,
  onRemoveAttachment,
  uploadingAttachment,
  attachmentInputRef,
  onAttachmentPick,
  isDraggingFiles,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDrop,
  onPaste,
  patientDisplayName,
  isGeneralDraft,
  scopeRemoveDisabled,
  scopeSubject = null,
  scopeEntityName,
  onScopeRestore,
  onScopeRemove,
  isThreadsOpen,
  onAddContext,
  onSend,
  onScrollToBottom,
  className,
}) => {
  const { formatMessage } = useIntl();
  const fieldRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // The scope pill (and its detach/reattach control) always covers patient
  // and entity together; an entity-bound page shows the entity chip next
  // to the patient chip. A plain patient page keeps the pill alone.
  const entity = isEntitySubject(scopeSubject) ? scopeSubject : null;
  // Primitive identity for effects: the subject object is rebuilt every
  // render, so an object dep would tear down and recreate observers
  // constantly.
  const entityKey = entity ? `${entity.type}:${entity.id}` : '';
  const entityLabel = entity
    ? scopeEntityName ||
      formatMessage(
        entity.type === 'report'
          ? patientAgentMessages.scopeEntityReport
          : entity.type === 'document'
            ? patientAgentMessages.scopeEntityDocument
            : patientAgentMessages.scopeEntityEncounter,
      )
    : '';

  const restoreContextLabel = formatMessage(
    patientAgentMessages.scopeRestoreHint,
    {
      context: entity
        ? `${patientDisplayName} / ${entityLabel}`
        : patientDisplayName,
    },
  );

  // A combined chip must never wrap. When its flex slot is too narrow
  // (tight rails), the patient's full name is dropped and the avatar
  // initials keep identifying the patient. One-way for the current mount:
  // the chip re-measures on the next mount.
  const scopePillRef = useRef<HTMLSpanElement>(null);
  const [nameDropped, setNameDropped] = useState(false);
  useEffect(() => {
    const pill = scopePillRef.current;
    if (!entityKey || !pill || nameDropped) {
      return;
    }
    const observer = new ResizeObserver(() => {
      if (pill.scrollWidth > pill.clientWidth) {
        setNameDropped(true);
      }
    });
    observer.observe(pill);
    return () => observer.disconnect();
  }, [entityKey, nameDropped]);

  return (
    <div
      className={cn(
        styles.composer,
        isThreadsOpen && styles.composerHidden,
        className,
      )}
      data-component="agent-chat-composer"
    >
      <div
        ref={fieldRef}
        className={cn(styles.composerField, isDraggingFiles && styles.dragging)}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onPaste={onPaste}
      >
        {onScrollToBottom ? (
          <button
            type="button"
            className={styles.scrollToBottom}
            aria-label={formatMessage(patientAgentMessages.scrollToBottom)}
            title={formatMessage(patientAgentMessages.scrollToBottom)}
            onClick={onScrollToBottom}
          >
            <Icon name="arrowDown" size={20} />
          </button>
        ) : null}
        <input
          ref={attachmentInputRef}
          type="file"
          multiple
          hidden
          onChange={onAttachmentPick}
        />
        {/* The row (and its padding) renders only when there are attachments,
            so the composer geometry is unchanged while it is empty. */}
        {pendingAttachments.length > 0 && (
          <div className={styles.composerAttachments}>
            {pendingAttachments.map((attachment) =>
              attachment.previewUrl ? (
                /* Images render as a bare square preview tile — the thumbnail
                   is the whole widget, no document-style name label. */
                <div
                  key={attachment.id}
                  className={cn(
                    styles.attachmentTile,
                    attachment.uploading && styles.attachmentTileUploading,
                  )}
                  title={attachment.name}
                >
                  <img src={attachment.previewUrl} alt="" />
                  {attachment.uploading ? (
                    <Spinner
                      size={14}
                      className={styles.attachmentTileSpinner}
                    />
                  ) : null}
                  <button
                    type="button"
                    className={cn(
                      styles.attachmentRemove,
                      styles.attachmentRemoveOnTile,
                    )}
                    aria-label={formatMessage(
                      patientAgentMessages.removeAttachment,
                    )}
                    onClick={() => onRemoveAttachment(attachment.id)}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div key={attachment.id} className={styles.attachmentCard}>
                  <div className={styles.attachmentThumb}>
                    <Icon name="doc" size={20} />
                  </div>
                  <div className={styles.attachmentCardInfo}>
                    <span
                      className={styles.attachmentCardName}
                      title={attachment.name}
                    >
                      {attachment.name}
                    </span>
                    {attachment.uploading ? (
                      <span className={styles.attachmentCardStatus}>
                        {formatMessage(
                          patientAgentMessages.uploadingAttachment,
                        )}
                      </span>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className={styles.attachmentRemove}
                    aria-label={formatMessage(
                      patientAgentMessages.removeAttachment,
                    )}
                    onClick={() => onRemoveAttachment(attachment.id)}
                  >
                    ×
                  </button>
                </div>
              ),
            )}
          </div>
        )}
        {isDraggingFiles ? (
          <div className={styles.dropHint}>
            <span>{formatMessage(patientAgentMessages.dropFiles)}</span>
          </div>
        ) : null}
        {/* The textarea stays enabled while the agent runs — only the send
            button is disabled, so the user can keep typing and the caret
            never leaves the composer. */}
        <Textarea
          value={inputValue}
          rows={documentMode || floating || isShell ? 1 : 3}
          autoFocus={floating}
          isElastic
          isResizeDisabled
          placeholder={formatMessage(patientAgentMessages.composerPlaceholder)}
          className={styles.textareaWrapper}
          textareaClassName={styles.textarea}
          outerRef={textareaRef}
          onChange={(event) => onInputChange(event.target.value)}
          onKeyDown={onKeyDown}
        />
        <ComposerScrollbar fieldRef={fieldRef} textareaRef={textareaRef} />
        <div className={styles.composerToolbar}>
          {isShell && patientDisplayName && isGeneralDraft ? (
            <button
              type="button"
              className={styles.scopeRestore}
              title={restoreContextLabel}
              aria-label={restoreContextLabel}
              onClick={onScopeRestore}
            >
              <Icon name="plus" size={12} />
              <span>
                {entity
                  ? `${patientDisplayName} · ${entityLabel}`
                  : patientDisplayName}
              </span>
            </button>
          ) : null}
          {isShell && patientDisplayName && !isGeneralDraft ? (
            <span
              ref={scopePillRef}
              className={cn(
                styles.scopePill,
                scopeRemoveDisabled && styles.scopePillNoRemove,
              )}
              title={
                entity
                  ? `${patientDisplayName} · ${entityLabel}`
                  : patientDisplayName
              }
            >
              <span className={styles.scopePillAvatar}>
                {patientDisplayName
                  .split(' ')
                  .map((part) => part[0])
                  .filter(Boolean)
                  .slice(0, 2)
                  .join('')}
              </span>
              <span
                className={cn(
                  styles.scopePillName,
                  entity && nameDropped && styles.scopePillNameDropped,
                )}
              >
                {patientDisplayName}
              </span>
              {entity ? (
                <>
                  <span className={styles.scopePillDivider}>·</span>
                  <span className={styles.scopePillEntity}>{entityLabel}</span>
                </>
              ) : null}
              {!scopeRemoveDisabled ? (
                <button
                  type="button"
                  className={styles.scopePillRemove}
                  aria-label={formatMessage(
                    entity
                      ? patientAgentMessages.scopeDetachAllHint
                      : patientAgentMessages.removePatientScope,
                  )}
                  title={formatMessage(
                    entity
                      ? patientAgentMessages.scopeDetachAllHint
                      : patientAgentMessages.scopeRemoveHint,
                  )}
                  onClick={onScopeRemove}
                >
                  ×
                </button>
              ) : null}
            </span>
          ) : null}
          <button
            type="button"
            className={styles.composerAttachButton}
            aria-label={formatMessage(patientAgentMessages.attachFile)}
            disabled={uploadingAttachment || isRunning || isResolving}
            onClick={() => attachmentInputRef.current?.click()}
          >
            {uploadingAttachment ? (
              <Spinner size={14} />
            ) : (
              <Icon name="plus" size={18} />
            )}
          </button>
          {!documentMode && !floating && !isShell ? (
            <div className={styles.composerContext}>
              <span>{formatMessage(patientAgentMessages.patientRecord)}</span>
              <button type="button" onClick={onAddContext}>
                + {formatMessage(patientAgentMessages.addContext)}
              </button>
            </div>
          ) : null}
          <Button
            className={styles.sendButton}
            size="medium"
            circle
            variant="primary"
            onClick={() => {
              onSend();
              // The click gives focus to the button; put the caret back
              // into the composer so typing can continue immediately.
              textareaRef.current?.focus();
            }}
            aria-label={formatMessage(patientAgentMessages.composerSend)}
            disabled={
              isResolving ||
              isRunning ||
              pendingAttachments.some((attachment) => attachment.uploading) ||
              !inputValue.trim()
            }
            loading={isRunning}
            icon="arrowUp"
          />
        </div>
      </div>
    </div>
  );
};
